import { useMutation } from "@tanstack/react-query";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";

/** The four theme tokens written into `settings.colors` (extracted path). */
export type BrandColorTokens = {
  primary: string;
  accent: string;
  surface: string;
  ink: string;
};

/** Body of `POST /api/seller/theme/apply-brand-colors`. Mirrors the backend
 *  `ApplyBrandColorsDto`. The endpoint derives store + seller from the session
 *  (`request.seller`) exactly like the other `seller/theme` routes, so there is
 *  no slug in the URL or body.
 *
 *  - `theme: 'extracted'` → `colors` required; applied to live `settings.colors`.
 *  - `theme: 'neutral'`   → metadata only; `colors` omitted, `accent` null. */
export type ApplyBrandColorsInput = {
  theme: "extracted" | "neutral";
  colors?: BrandColorTokens;
  /** 1–5 extracted hex swatches (metadata). */
  palette: string[];
  accent?: string | null;
};

export function useApplyBrandColorsMutation() {
  const mutation = useMutation({
    mutationFn: (input: ApplyBrandColorsInput) =>
      apiJsonClient<unknown>(endpoints.seller.themeApplyBrandColors, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
